import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Send, MessageSquare } from 'lucide-react';
import { settingsAPI, messagesAPI } from '@/lib/api';
import { toast } from 'sonner';

const ContactAdminModal = ({ open, onOpenChange }) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [adminInfo, setAdminInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open) {
            fetchAdminInfo();
        }
    }, [open]);

    const fetchAdminInfo = async () => {
        try {
            setLoading(true);
            const res = await settingsAPI.getContactAdmin();
            setAdminInfo(res.data);
        } catch (error) {
            console.error('Error fetching admin info:', error);
            toast.error('Eroare la încărcarea informațiilor');
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!message.trim()) {
            toast.error('Te rugăm să scrii un mesaj');
            return;
        }

        if (!adminInfo?.admin_user_id) {
            toast.error('Nu există un administrator configurat pentru contact');
            return;
        }

        setSending(true);
        try {
            // Start a new support conversation with the admin
            await messagesAPI.sendNew({
                recipient_id: adminInfo.admin_user_id,
                content: message.trim(),
                conversation_type: 'support'
            });

            toast.success('Mesajul a fost trimis către administrator');
            setMessage('');
            onOpenChange(false);
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea mesajului');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        Contactează Administratorul
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                ) : !adminInfo?.admin_user_id ? (
                    <div className="py-4 text-center text-muted-foreground">
                        <p>Nu există un administrator configurat pentru contact.</p>
                        <p className="text-sm mt-2">Te rugăm să încerci mai târziu.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground">
                                Mesajul va fi trimis către: <span className="font-medium text-foreground">{adminInfo.admin_name || adminInfo.admin_username}</span>
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="message">Mesajul tău</Label>
                            <Textarea
                                id="message"
                                placeholder="Descrie problema sau întrebarea ta..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={5}
                                className="resize-none"
                                disabled={sending}
                            />
                            <p className="text-xs text-muted-foreground">
                                Răspunsul va fi trimis în secțiunea de mesaje.
                            </p>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={sending}
                    >
                        Anulează
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={sending || loading || !adminInfo?.admin_user_id || !message.trim()}
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Se trimite...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-2" />
                                Trimite mesajul
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ContactAdminModal;
